/* Tutorial progress store (DESIGN-tutorial-system.md §1.2).
 *
 * `{ chapter, step, dismissed, completed }` per series, mirrored to the server
 * (User.tutorialState via PATCH /auth/me, debounced) so progress survives
 * devices, with a localStorage fallback for offline. Auto-start decisions live
 * in the host components; this store never locks anything — every mutation is
 * user- or predicate-driven.
 *
 * Instrumentation: every transition is emitted into `logSink` (set by the host
 * with the right team context) and lands in ActivityLog type:'tutorial' — the
 * pilot's step-level drop-off data. Fire-and-forget; losing an event is fine. */
import { create } from 'zustand';
import type { TutorialState as PersistedTutorialState } from '@venturewrist/shared';
import { api } from '../lib/api';
import type { TutSeriesId } from './types';

const LS_KEY = 'vw.tutorial.v1';

export interface TutLogEvent {
  event: 'start' | 'restart' | 'step' | 'back' | 'dismiss' | 'complete';
  series: TutSeriesId;
  chapter: number;
  step: number;
}

interface TutorialStore {
  hydrated: boolean;
  /** Per-user localStorage key (set at hydrate) so shared machines don't leak
   *  one account's progress into another. */
  lsKey: string;
  /** Currently-running series (null = no overlay). Identifies the *content*
   *  (drives the UI + log events). */
  active: TutSeriesId | null;
  /** Persistence key for the active run. Distinct from `active` so a series can
   *  be scoped — the student tour is keyed per team (`student:<teamId>`) so a
   *  student on a new team plays the tour again on their first quarter. */
  pkey: string;
  chapter: number;
  step: number;
  /** Steps per chapter of the active series. */
  shape: number[];
  /** Final step passed this session — spotlight hides, finale panel shows. */
  finished: boolean;
  progress: PersistedTutorialState;
  /** Session-sticky task completion (task ids that have been observed true). */
  achieved: Record<string, true>;
  /** Event counters screens emit via tutSignal(). */
  signals: Record<string, number>;
  panelCollapsed: boolean;
  logSink: ((e: TutLogEvent) => void) | null;

  hydrate: (server: PersistedTutorialState | null | undefined, userId: string) => void;
  /** Clear session state on logout so the next account starts clean. */
  reset: () => void;
  /** Start or resume a series. No-op if dismissed/completed (use restart).
   *  `key` is the persistence key (defaults to the series id; pass a scoped key
   *  like `student:<teamId>` to track progress per team). */
  start: (series: TutSeriesId, shape: number[], key?: string) => void;
  /** Restart from the top ("Restart tour" in the ? menu). */
  restart: (series: TutSeriesId, shape: number[], key?: string) => void;
  next: () => void;
  back: () => void;
  /** "Skip tour — I'll explore": never auto-starts again. */
  dismiss: () => void;
  /** Hide the finale panel / unmount cleanly. */
  stop: () => void;
  markAchieved: (id: string) => void;
  signal: (name: string) => void;
  setPanelCollapsed: (v: boolean) => void;
  setLogSink: (fn: ((e: TutLogEvent) => void) | null) => void;
}

function loadLocal(key: string): PersistedTutorialState {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw) as PersistedTutorialState;
  } catch { /* ignore */ }
  return {};
}

let saveTimer: ReturnType<typeof setTimeout> | undefined;
function persist(progress: PersistedTutorialState, key: string) {
  try { localStorage.setItem(key, JSON.stringify(progress)); } catch { /* ignore */ }
  // Debounced server mirror — rapid Next clicks collapse into one PATCH.
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    api.updatePreferences({ tutorialState: progress }).catch(() => { /* offline: localStorage holds it */ });
  }, 800);
}

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

export const useTutorial = create<TutorialStore>((set, get) => {
  const emit = (e: TutLogEvent) => { try { get().logSink?.(e); } catch { /* ignore */ } };

  const record = (key: string, patch: Partial<PersistedTutorialState[string]>) => {
    const prev = get().progress[key] ?? { chapter: 0, step: 0, dismissed: false, completed: false };
    const progress = { ...get().progress, [key]: { ...prev, ...patch } };
    set({ progress });
    persist(progress, get().lsKey);
  };

  return {
    hydrated: false,
    lsKey: LS_KEY,
    active: null,
    pkey: '',
    chapter: 0,
    step: 0,
    shape: [],
    finished: false,
    progress: {},
    achieved: {},
    signals: {},
    panelCollapsed: false,
    logSink: null,

    hydrate: (server, userId) => {
      if (get().hydrated) return;
      const lsKey = `${LS_KEY}:${userId}`;
      // Per-series merge; the server copy wins (it followed the user here).
      set({ hydrated: true, lsKey, progress: { ...loadLocal(lsKey), ...(server ?? {}) } });
    },

    reset: () => {
      clearTimeout(saveTimer);
      set({
        hydrated: false, lsKey: LS_KEY, active: null, pkey: '', chapter: 0, step: 0,
        shape: [], finished: false, progress: {}, achieved: {}, signals: {}, logSink: null,
      });
    },

    start: (series, shape, key = series) => {
      const p = get().progress[key];
      if (p?.dismissed || p?.completed) return;
      const chapter = clamp(p?.chapter ?? 0, 0, shape.length - 1);
      const step = clamp(p?.step ?? 0, 0, (shape[chapter] ?? 1) - 1);
      set({ active: series, pkey: key, shape, chapter, step, finished: false, achieved: {} });
      if (!p) record(key, { chapter, step });
      emit({ event: 'start', series, chapter, step });
    },

    restart: (series, shape, key = series) => {
      record(key, { chapter: 0, step: 0, dismissed: false, completed: false });
      set({ active: series, pkey: key, shape, chapter: 0, step: 0, finished: false, achieved: {} });
      emit({ event: 'restart', series, chapter: 0, step: 0 });
    },

    next: () => {
      const { active, pkey, chapter, step, shape } = get();
      if (!active) return;
      const last = (shape[chapter] ?? 1) - 1;
      if (step < last) {
        set({ step: step + 1 });
        record(pkey, { chapter, step: step + 1 });
        emit({ event: 'step', series: active, chapter, step: step + 1 });
      } else if (chapter < shape.length - 1) {
        set({ chapter: chapter + 1, step: 0 });
        record(pkey, { chapter: chapter + 1, step: 0 });
        emit({ event: 'step', series: active, chapter: chapter + 1, step: 0 });
      } else {
        set({ finished: true });
        record(pkey, { completed: true });
        emit({ event: 'complete', series: active, chapter, step });
      }
    },

    back: () => {
      const { active, pkey, chapter, step, shape } = get();
      if (!active) return;
      if (step > 0) set({ step: step - 1 });
      else if (chapter > 0) set({ chapter: chapter - 1, step: (shape[chapter - 1] ?? 1) - 1 });
      else return;
      const s = get();
      record(pkey, { chapter: s.chapter, step: s.step });
      emit({ event: 'back', series: active, chapter: s.chapter, step: s.step });
    },

    dismiss: () => {
      const { active, pkey, chapter, step } = get();
      if (!active) return;
      record(pkey, { dismissed: true });
      emit({ event: 'dismiss', series: active, chapter, step });
      set({ active: null, finished: false });
    },

    stop: () => set({ active: null, finished: false }),

    markAchieved: (id) => {
      if (get().achieved[id]) return;
      set((s) => ({ achieved: { ...s.achieved, [id]: true } }));
    },

    signal: (name) =>
      set((s) => ({ signals: { ...s.signals, [name]: (s.signals[name] ?? 0) + 1 } })),

    setPanelCollapsed: (v) => set({ panelCollapsed: v }),
    setLogSink: (fn) => set({ logSink: fn }),
  };
});

/** Screens report tutorial-relevant events without subscribing to the store
 *  (e.g. tutSignal('design-edit') from a component-pill click). */
export const tutSignal = (name: string): void => {
  useTutorial.getState().signal(name);
};
